import { FIELD_KEYS } from "@shared/constants/fields";
import { PERSONAL_TEMPLATE, TENURE_TEMPLATE } from "@shared/interview/board-template";
import { SegmentCell } from "./InterviewCells";
import { saveInBackground } from "./saveFailure";
import { useHome, type Tenure } from "./ch2/useHome";
import { useAboutYou } from "./ch2/Ch2Screens";
import { StillHereLine } from "./ch2/Ch2Engine";

/**
 * s237 HOME-REDESIGN-1 PR 3 (D-236.47(1)(3)(4), D-236.88-1, -6, -8; board `Main.dc.html`, the `you` block) — "Is this
 * home…" under the home address in Personal details: Owned · Rented · With family, or in a care home, as three joined
 * buttons, and ONE note under them that follows the pick (rule 145's colour, warm-muted #4A4640).
 *
 *  • The answer is About You's home_tenure — stored on the "you" person (persons.home_tenure, PR #214).
 *  • Owned / Rented create or flag the home row (row 1 "Primary residence" of the matching chapter-two table, rule 153);
 *    With family creates no row (D-236.47(3)). All through the ONE home hook chapter two uses (useHome, rule 159).
 *  • No default (D-236.88-8): unanswered shows the three buttons and no note. The board draws no unanswered state.
 *  • Changing the answer asks about the old home: "Do you still own / rent <address>? Keep · Remove" (D-235.65).
 */
export function HomeTenureQuestion({ clientId, onAnswered }: { clientId: string; onAnswered: () => void }) {
  const about = useAboutYou(clientId, onAnswered);
  const home = useHome(clientId, onAnswered, about);
  if (!home.ready) return null;
  const label = PERSONAL_TEMPLATE.home_tenure.label;
  const tid = `interview-you-${FIELD_KEYS.HOME_TENURE}`;
  const options = (Object.keys(TENURE_TEMPLATE.options) as Tenure[]).map((t) => ({ value: t, label: TENURE_TEMPLATE.options[t] }));
  const note = home.tenure ? TENURE_TEMPLATE.notes[home.tenure] : null;
  return (
    <div className="flex flex-col gap-[7px]" data-testid={`${tid}-field`}>
      <label className="text-[13px] text-warm-charcoal">{label}</label>
      <SegmentCell fit value={home.tenure ?? ""} options={options} ariaLabel={label}
        onCommit={(t) => saveInBackground(home.pick(t as Tenure))} testId={tid} />
      {note && <span className="text-[13px] text-warm-muted" data-testid={`${tid}-note`}>{note}</span>}
      {home.stale.map((x) => (
        <StillHereLine key={`${x.table}-${x.row.rowId}`} question={x.question} testId={`${tid}-still-${x.table}-${x.row.rowId}`}
          onKeep={() => saveInBackground(home.keep(x))} onRemove={() => saveInBackground(home.remove(x))} />
      ))}
    </div>
  );
}
